import { parseCollectorUserNotificationPrefs } from './notificationPreferences';
import {
  Address,
  BasicInfo,
  ClientTag,
  Language,
  UserProfile,
} from './types/UserProfile';

type Loose = Record<string, unknown>;

function asRecord(v: unknown): Loose | null {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Loose;
}

function pickString(source: Loose, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = source[k];
    if (v !== undefined && v !== null && String(v).trim() !== '') {
      return String(v);
    }
  }
  return undefined;
}

function normalizeClients(raw: unknown): ClientTag[] | undefined {
  if (raw == null) return undefined;
  if (Array.isArray(raw)) {
    return raw.length ? (raw as ClientTag[]) : undefined;
  }
  const one = asRecord(raw);
  if (one && 'id' in one) return [raw as ClientTag];
  return undefined;
}

/** When API returns only numeric client ids (no client objects). */
function clientTagsFromIds(raw: unknown): ClientTag[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.map((id) => ({ id: Number(id), name: '' }));
}

function buildAddress(d: Loose): Address | undefined {
  const nested = asRecord(d.address);
  if (nested) {
    return {
      address1: pickString(nested, ['address1', 'address_1', 'street', 'line1']),
      address2: pickString(nested, ['address2', 'address_2', 'line2']),
      city: pickString(nested, ['city']),
      state: pickString(nested, ['state', 'region']),
      zip_code: pickString(nested, ['zip_code', 'zipcode', 'postal_code', 'zip']),
    };
  }
  const flat: Address = {
    address1: pickString(d, ['address1', 'address_1', 'street', 'line1']),
    address2: pickString(d, ['address2', 'address_2', 'line2']),
    city: pickString(d, ['city']),
    state: pickString(d, ['state', 'region']),
    zip_code: pickString(d, ['zip_code', 'zipcode', 'postal_code', 'zip']),
  };
  const hasAny = Object.values(flat).some(Boolean);
  return hasAny ? flat : undefined;
}

/**
 * Maps GET /collector/users/:id (and similar) payloads into UserProfile shape
 * expected by collector forms (basic_info, address, clients).
 */
export function normalizeCollectorUserResponse(raw: unknown): UserProfile {
  if (raw == null) {
    throw new Error('No user payload');
  }

  const root = asRecord(raw) ?? {};
  const d =
    asRecord(root.user) ??
    asRecord(root.profile) ??
    asRecord(root.data) ??
    root;

  const biNested =
    asRecord(d.basic_info) ??
    asRecord(d.basic_information) ??
    asRecord(d.basicInfo);

  const biSource: Loose = { ...d, ...(biNested ?? {}) };

  const basic_info: BasicInfo = {
    first_name: pickString(biSource, ['first_name', 'firstName']),
    middle_name: pickString(biSource, ['middle_name', 'middleName']),
    last_name: pickString(biSource, ['last_name', 'lastName']),
    preferred_name: pickString(biSource, ['preferred_name', 'preferredName']),
    date_of_birth: pickString(biSource, [
      'date_of_birth',
      'dateOfBirth',
      'dob',
      'birth_date',
    ]),
    gender: pickString(biSource, ['gender']),
    gender_identity: pickString(biSource, ['gender_identity', 'genderIdentity']),
    pronouns: pickString(biSource, ['pronouns']),
    languages: (biNested?.languages ?? d.languages) as BasicInfo['languages'],
  };

  const address = buildAddress(d);

  let clients =
    normalizeClients(d.clients) ??
    normalizeClients(d.client_tags) ??
    normalizeClients(d.client_tag) ??
    normalizeClients((d as Loose).collector_client_tags);
  if (!clients?.length) {
    clients =
      clientTagsFromIds(d.client_ids) ??
      clientTagsFromIds((d as Loose).collector_client_ids);
  }

  const email =
    pickString(d, ['email']) ??
    (biNested ? pickString(biNested, ['email']) : undefined);
  const phone_number =
    pickString(d, ['phone_number', 'phoneNumber', 'phone', 'mobile']);

  const notif = parseCollectorUserNotificationPrefs(raw, d);


  const merged: UserProfile = {
    ...(d as UserProfile),
    email: email ?? (d as UserProfile).email,
    phone_number: phone_number ?? (d as UserProfile).phone_number,
    basic_info,
    address,
    clients,
    email_opt_in: notif.email_opt_in,
    sms_opt_in: notif.sms_opt_in,
  };

  return merged;
}

/**
 * API may return language ids (numbers/strings) or full Language objects.
 * Multi-select expects full objects from the catalog.
 */
export function resolveLanguageSelection(raw: unknown, catalog: Language[]): Language[] {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return [];
  const first = raw[0];
  if (
    first != null &&
    typeof first === 'object' &&
    'id' in first &&
    'name' in (first as object)
  ) {
    return raw as Language[];
  }
  const ids = new Set(raw.map((x) => String(x)));
  return catalog.filter((l) => ids.has(String(l.id)));
}
